import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ProcessedFile } from '@/lib/fileUploadUtils';

interface UploadContextType {
  uploadedFiles: ProcessedFile[];
  addUploadedFiles: (files: ProcessedFile[]) => void;
  clearUploadedFiles: () => void;
  removeUploadedFile: (fileName: string) => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (context === undefined) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};

interface UploadProviderProps {
  children: ReactNode;
}

export const UploadProvider: React.FC<UploadProviderProps> = ({ children }) => {
  const [uploadedFiles, setUploadedFiles] = useState<ProcessedFile[]>([]);

  const addUploadedFiles = (files: ProcessedFile[]) => {
    setUploadedFiles(prev => {
      const byName = new Set(prev.map(f => f.name));
      return [...prev, ...files.filter(f => !byName.has(f.name))];
    });
  };

  const clearUploadedFiles = () => {
    setUploadedFiles([]);
  };

  const removeUploadedFile = (fileName: string) => {
    setUploadedFiles(prev => prev.filter(file => file.name !== fileName));
  };

  const value: UploadContextType = {
    uploadedFiles,
    addUploadedFiles,
    clearUploadedFiles,
    removeUploadedFile,
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
}; 